import type {Permission, PermissionType} from '@hconnect/apiclient'
import {parseDataScope} from '@hconnect/common/hproduce/whiteListCheck'
import {
  getPlantIdsPerCountryMap,
  type PlantIdsPerCountryMap,
  type UpmPlantsContextValue,
  useUpmPlantsContext
} from '@hconnect/upm'
import {usePermissionsQuery} from '@hproduce/permissions'
import {orderBy} from 'lodash'
import {useMemo} from 'react'

const rcfaCreatePermission: PermissionType = 'CREATE_ROOT_CAUSE_FAILURE_ANALYSES'

export interface RcfaCreatePlantOption {
  plantId: string
  plantName: string
  plantUpmId: string
}

interface UseRcfaCreatePlantOptionsResult {
  options: RcfaCreatePlantOption[]
  isLoading: boolean
}

export const getPlantIdsForRcfaCreatePermissions = (
  permissions: Permission[],
  plantIdsForCountryMap: PlantIdsPerCountryMap
): string[] => {
  const plantIds = new Set<string>()

  permissions
    .filter(({permissionType}) => permissionType === rcfaCreatePermission)
    .forEach(({dataScope}) => {
      const {plantId, countryId} = parseDataScope(dataScope)

      if (plantId) {
        plantIds.add(plantId)
      } else if (countryId) {
        plantIdsForCountryMap[countryId]?.forEach((id) => plantIds.add(id))
      }
    })

  return Array.from(plantIds)
}

export const getRcfaCreatePlantOptions = (
  permissions: Permission[],
  upmPlants: UpmPlantsContextValue
): RcfaCreatePlantOption[] => {
  const plantIdsForCountryMap = getPlantIdsPerCountryMap(upmPlants)
  const plantIds = getPlantIdsForRcfaCreatePermissions(permissions, plantIdsForCountryMap)

  return orderBy(
    plantIds.flatMap((plantId) => {
      const upmPlant = upmPlants[plantId]

      if (!upmPlant?.upmId) {
        return []
      }

      return [
        {
          plantId,
          plantName: upmPlant.name || plantId,
          plantUpmId: upmPlant.upmId
        }
      ]
    }),
    'plantName'
  )
}

export const useRcfaCreatePlantOptions = (): UseRcfaCreatePlantOptionsResult => {
  const {data: permissions = [], isPending: isLoadingPermissions} = usePermissionsQuery()
  const upmPlants = useUpmPlantsContext({isInitialLoad: true})

  const options = useMemo(
    () => (upmPlants ? getRcfaCreatePlantOptions(permissions, upmPlants) : []),
    [permissions, upmPlants]
  )

  return {
    options,
    isLoading: isLoadingPermissions || !upmPlants
  }
}
